import { Injectable, signal } from '@angular/core';
import { ServiceBusEntity } from './models';

// "Unreadable" stands for every entity that could not be read: not found, no access or an error.
export type ServiceBusStatusKey = 'DeadLettered' | 'Clean' | 'Unreadable';

export const SERVICE_BUS_FILTERS: {
  status: ServiceBusStatusKey;
  label: string;
  always: boolean;
}[] = [
  { status: 'DeadLettered', label: 'Dead letters', always: true },
  { status: 'Clean', label: 'No dead letters', always: true },
  { status: 'Unreadable', label: 'Could not be read', always: false },
];

export function statusKey(entity: ServiceBusEntity): ServiceBusStatusKey {
  return entity.status === 'DeadLettered' || entity.status === 'Clean'
    ? entity.status
    : 'Unreadable';
}

export interface ServiceBusViewParams {
  query: string;
  selected: ReadonlySet<ServiceBusStatusKey>;
  namespace: string;
}

// Kept for the whole session, so the search and filters are still there when the reader comes back from the other tab.
@Injectable({ providedIn: 'root' })
export class ServiceBusViewState {
  private readonly initial = readServiceBusViewFromUrl();

  readonly query = signal(this.initial.query);
  // Nothing selected means every kind of entity is shown.
  readonly selected = signal<ReadonlySet<ServiceBusStatusKey>>(this.initial.selected);
  // An empty name means every namespace.
  readonly namespace = signal(this.initial.namespace);
}

const QUERY_PARAM = 'sbq';
const STATUS_PARAM = 'sbstatus';
const NAMESPACE_PARAM = 'sbns';

// ?sbq=drc&sbstatus=DeadLettered&sbns=sb-sjp-drc-dev makes a view shareable and survives a reload.
export function readServiceBusViewFromUrl(): ServiceBusViewParams {
  const params = new URLSearchParams(window.location.search);
  const known = new Set<string>(SERVICE_BUS_FILTERS.map((filter) => filter.status));
  return {
    query: params.get(QUERY_PARAM) ?? '',
    selected: new Set(
      (params.get(STATUS_PARAM) ?? '')
        .split(',')
        .filter((value): value is ServiceBusStatusKey => known.has(value)),
    ),
    namespace: params.get(NAMESPACE_PARAM) ?? '',
  };
}

export function writeServiceBusViewToUrl(state: ServiceBusViewParams): void {
  updateUrl((url) => {
    setParam(url, QUERY_PARAM, state.query.trim() === '' ? null : state.query);
    setParam(url, STATUS_PARAM, state.selected.size === 0 ? null : [...state.selected].join(','));
    setParam(url, NAMESPACE_PARAM, state.namespace === '' ? null : state.namespace);
  });
}

function updateUrl(change: (url: URL) => void): void {
  const url = new URL(window.location.href);
  change(url);
  window.history.replaceState(null, '', url);
}

function setParam(url: URL, name: string, value: string | null): void {
  if (value) {
    url.searchParams.set(name, value);
  } else {
    url.searchParams.delete(name);
  }
}

// What has dead letters first, most of them first; what could not be read next; then the rest by name.
const RANK: Record<ServiceBusStatusKey, number> = { DeadLettered: 0, Unreadable: 1, Clean: 2 };

export function byAttention(a: ServiceBusEntity, b: ServiceBusEntity): number {
  const collator = { sensitivity: 'base' } as const;
  return (
    RANK[statusKey(a)] - RANK[statusKey(b)] ||
    b.deadLetterMessageCount - a.deadLetterMessageCount ||
    a.namespace.localeCompare(b.namespace, undefined, collator) ||
    a.name.localeCompare(b.name, undefined, collator)
  );
}
